// Content Sources — per-brand editable cascades for every meta field the
// titling pipeline consumes. The backend ships a default cascade for each
// field (see services/metaCascadeConfig.js); this card lets an operator
// re-point the priority ladder per brand (Brand.metaCascades). Save writes
// only the fields the operator actually customized; "Reset to default"
// deletes that field's override so it inherits again.
//
// Source shape per row:
//   { type: 'doc',     doc: <whitelist>, path: string, prefix?, suffix? }
//   { type: 'literal', value: string }
//
// Backend validation runs on PATCH; per-field errors surface via toast.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardBody, VStack, HStack, Heading, Text, Button, Input, Select,
  IconButton, Badge, Divider, Collapse, Icon, useToast,
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';

type SourceType = 'doc' | 'literal';
type Source =
  | { type: 'doc'; doc: string; path: string; prefix?: string; suffix?: string }
  | { type: 'literal'; value: unknown };

type FieldRow = {
  field: string;
  label: string;
  effective: Source[];
  default: Source[];
  isOverridden: boolean;
};

type CascadesResponse = {
  fields: FieldRow[];
  contextDocs: string[];
};

type Props = { brandId: string };

export function MetaCascadesCard({ brandId }: Props) {
  const toast = useToast();
  const [state, setState] = useState<CascadesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Working copy — a sparse map of { field → sources[] } for fields the
  // operator has EDITED this session (excludes untouched defaults so we
  // save the minimum). Rebuilt on load / reset.
  const [working, setWorking] = useState<Record<string, Source[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiJson<CascadesResponse>(`/api/brand/${brandId}/meta-cascades`);
      setState(data);
      // Seed working copy with the effective cascades of fields that are
      // ALREADY overridden — so the initial UI matches what would render,
      // and unedited defaults stay implicit (saving them empty would
      // pollute Brand.metaCascades with copies of the shipped defaults).
      const seed: Record<string, Source[]> = {};
      for (const f of data.fields) if (f.isOverridden) seed[f.field] = cloneSources(f.effective);
      setWorking(seed);
    } catch (err) {
      toast({ status: 'error', title: 'Failed to load cascades', description: (err as Error).message });
    } finally {
      setLoading(false);
    }
  }, [brandId, toast]);

  useEffect(() => { load(); }, [load]);

  const rows = state?.fields ?? [];
  const docs = state?.contextDocs ?? [];

  const dirty = useMemo(() => {
    if (!state) return false;
    // Compare each field's working copy to what the server currently
    // has on file. Absence in either side means "same as default" —
    // dirtiness only matters when the shape differs.
    const persistedSet = new Set(rows.filter(r => r.isOverridden).map(r => r.field));
    const workingSet = new Set(Object.keys(working));
    if (persistedSet.size !== workingSet.size) return true;
    for (const k of workingSet) {
      if (!persistedSet.has(k)) return true;
      const persisted = rows.find(r => r.field === k)?.effective || [];
      if (!sourcesEqual(persisted, working[k])) return true;
    }
    return false;
  }, [state, rows, working]);

  // Current cascade for a field: working-copy override if edited this
  // session, otherwise the server's effective cascade.
  const currentFor = (field: string): Source[] => {
    if (Object.prototype.hasOwnProperty.call(working, field)) return working[field];
    const row = rows.find(r => r.field === field);
    return row?.effective ?? [];
  };

  const startEdit = (field: string) => {
    if (Object.prototype.hasOwnProperty.call(working, field)) return; // already editing
    const row = rows.find(r => r.field === field);
    setWorking(prev => ({ ...prev, [field]: cloneSources(row?.effective ?? []) }));
  };

  const updateSource = (field: string, idx: number, patch: Partial<Source>) => {
    startEdit(field);
    setWorking(prev => {
      const arr = [...(prev[field] || currentFor(field))];
      arr[idx] = { ...(arr[idx] as Source), ...patch } as Source;
      return { ...prev, [field]: arr };
    });
  };

  const moveSource = (field: string, idx: number, dir: -1 | 1) => {
    startEdit(field);
    setWorking(prev => {
      const arr = [...(prev[field] || currentFor(field))];
      const to = idx + dir;
      if (to < 0 || to >= arr.length) return prev;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return { ...prev, [field]: arr };
    });
  };

  const removeSource = (field: string, idx: number) => {
    startEdit(field);
    setWorking(prev => {
      const arr = [...(prev[field] || currentFor(field))];
      arr.splice(idx, 1);
      return { ...prev, [field]: arr };
    });
  };

  const addSource = (field: string, type: SourceType) => {
    startEdit(field);
    setWorking(prev => {
      const arr = [...(prev[field] || currentFor(field))];
      arr.push(type === 'doc'
        ? { type: 'doc', doc: docs[0] || 'ad', path: '' }
        : { type: 'literal', value: '' });
      return { ...prev, [field]: arr };
    });
  };

  const resetField = (field: string) => {
    // Removing the field from `working` implicitly reverts to inherit —
    // but only if the server ALSO doesn't have this field overridden.
    // If the server has an override on file, resetting means we need to
    // send an explicit "remove this key" — represented by working[field]
    // being absent AND the server's payload omitting the field. The
    // save flow handles this via comparison against `rows[].isOverridden`.
    setWorking(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Build the payload: everything in `working` + explicit "reset"
      // for fields the server currently overrides but the operator
      // wiped from `working`. Backend treats absence as "inherit
      // default", so leaving a field out is enough — we don't need
      // to send null.
      const payload: Record<string, Source[]> = { ...working };
      // Belt-and-suspenders: also propagate the persisted overrides that
      // the operator DIDN'T touch (keeps saves idempotent even if the
      // browser lost state between load and save).
      for (const r of rows) {
        if (r.isOverridden && !Object.prototype.hasOwnProperty.call(payload, r.field)) {
          payload[r.field] = cloneSources(r.effective);
        }
      }
      // Drop any accidental empty arrays — backend rejects them with a
      // clearer message than the operator wants at save time.
      for (const k of Object.keys(payload)) {
        if (!Array.isArray(payload[k]) || payload[k].length === 0) delete payload[k];
      }

      await apiJson(`/api/brand/${brandId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metaCascades: payload }),
      });
      toast({ status: 'success', title: 'Content sources saved' });
      await load();
    } catch (err) {
      toast({ status: 'error', title: 'Save failed', description: (err as Error).message, duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outline">
      <CardBody>
        <HStack mb={1} spacing={2}>
          <StackIcon />
          <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">Content Sources</Heading>
          <Badge fontSize="10px" ml={2} colorScheme="gray">{rows.length} fields</Badge>
        </HStack>
        <Text fontSize="xs" color="brand.muted" mb={4}>
          Which data source each titling field falls back to, in order. First non-empty value wins. Overrides
          replace the shipped default for that field entirely — untouched fields inherit.
        </Text>

        {loading ? (
          <Text fontSize="sm" color="brand.muted">Loading…</Text>
        ) : (
          <VStack align="stretch" spacing={0.5}>
            {rows.map(row => {
              const isOpen = !!expanded[row.field];
              const isDirty = Object.prototype.hasOwnProperty.call(working, row.field)
                && !sourcesEqual(row.effective, working[row.field]);
              const isCustom = row.isOverridden || Object.prototype.hasOwnProperty.call(working, row.field);
              return (
                <Box key={row.field} borderTop="1px solid" borderColor="brand.border" py={2}>
                  <HStack
                    spacing={2}
                    cursor="pointer"
                    onClick={() => setExpanded(p => ({ ...p, [row.field]: !p[row.field] }))}
                    _hover={{ bg: 'brand.canvas' }}
                    borderRadius="md"
                    px={1}
                    py={0.5}
                  >
                    <ChevronIcon open={isOpen} />
                    <Text fontSize="sm" fontWeight="600" flex="1" color="brand.text">{row.label}</Text>
                    <Text fontSize="10px" color="brand.muted" fontFamily="mono" mr={2}>{row.field}</Text>
                    {isDirty && <Badge colorScheme="orange" fontSize="9px">Edited</Badge>}
                    {isCustom && !isDirty && <Badge colorScheme="purple" fontSize="9px">Custom</Badge>}
                    <Badge colorScheme="gray" fontSize="9px" variant="subtle">
                      {currentFor(row.field).length} src
                    </Badge>
                  </HStack>

                  <Collapse in={isOpen} animateOpacity>
                    <Box pl={6} pt={2} pb={1}>
                      <SourceList
                        field={row.field}
                        sources={currentFor(row.field)}
                        docs={docs}
                        onUpdate={(idx, patch) => updateSource(row.field, idx, patch)}
                        onMove={(idx, dir) => moveSource(row.field, idx, dir)}
                        onRemove={(idx) => removeSource(row.field, idx)}
                      />
                      <HStack mt={2} spacing={2}>
                        <Button size="xs" variant="outline" onClick={() => addSource(row.field, 'doc')}>+ Doc source</Button>
                        <Button size="xs" variant="outline" onClick={() => addSource(row.field, 'literal')}>+ Literal</Button>
                        {isCustom && (
                          <Button size="xs" variant="ghost" colorScheme="red" onClick={() => resetField(row.field)}>
                            Reset to default
                          </Button>
                        )}
                      </HStack>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            <Divider my={2} />
            <HStack justify="flex-end" pt={1}>
              <Button size="sm" variant="outline" onClick={load} isDisabled={saving}>Reload</Button>
              <Button size="sm" variant="solid" colorScheme="purple" onClick={save} isLoading={saving} isDisabled={!dirty}>
                Save changes
              </Button>
            </HStack>
          </VStack>
        )}
      </CardBody>
    </Card>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

function SourceList({
  field, sources, docs, onUpdate, onMove, onRemove,
}: {
  field: string;
  sources: Source[];
  docs: string[];
  onUpdate: (idx: number, patch: Partial<Source>) => void;
  onMove:   (idx: number, dir: -1 | 1) => void;
  onRemove: (idx: number) => void;
}) {
  if (sources.length === 0) {
    return (
      <Text fontSize="xs" color="brand.muted" fontStyle="italic" py={1}>
        No sources — this field will be null at render time. Add one below.
      </Text>
    );
  }
  return (
    <VStack align="stretch" spacing={1.5}>
      {sources.map((src, idx) => (
        <SourceRow
          key={`${field}-${idx}`}
          idx={idx}
          total={sources.length}
          source={src}
          docs={docs}
          onUpdate={(patch) => onUpdate(idx, patch)}
          onUp={() => onMove(idx, -1)}
          onDown={() => onMove(idx, 1)}
          onRemove={() => onRemove(idx)}
        />
      ))}
    </VStack>
  );
}

function SourceRow({
  idx, total, source, docs, onUpdate, onUp, onDown, onRemove,
}: {
  idx: number;
  total: number;
  source: Source;
  docs: string[];
  onUpdate: (patch: Partial<Source>) => void;
  onUp: () => void; onDown: () => void; onRemove: () => void;
}) {
  const isDoc = source.type === 'doc';
  return (
    <HStack
      spacing={1.5}
      p={1.5}
      borderRadius="md"
      bg="brand.canvas"
      borderWidth="1px"
      borderColor="brand.border"
      align="center"
    >
      <Text fontSize="10px" fontFamily="mono" color="brand.muted" w="16px" textAlign="right">#{idx + 1}</Text>
      <Select
        size="xs"
        w="76px"
        value={source.type}
        onChange={e => {
          const nextType = e.target.value as SourceType;
          if (nextType === source.type) return;
          onUpdate(nextType === 'doc'
            ? { type: 'doc', doc: docs[0] || 'ad', path: '' } as Partial<Source>
            : { type: 'literal', value: '' } as Partial<Source>);
        }}
      >
        <option value="doc">Doc</option>
        <option value="literal">Literal</option>
      </Select>
      {isDoc ? (
        <>
          <Select
            size="xs"
            value={(source as { doc: string }).doc}
            onChange={e => onUpdate({ doc: e.target.value } as Partial<Source>)}
            w="180px"
          >
            {docs.map(d => <option key={d} value={d}>{d}</option>)}
          </Select>
          <Input
            size="xs"
            fontFamily="mono"
            placeholder="path.to.field"
            value={(source as { path: string }).path || ''}
            onChange={e => onUpdate({ path: e.target.value } as Partial<Source>)}
            flex="1"
            minW="140px"
          />
          <Input
            size="xs"
            placeholder="prefix"
            value={(source as { prefix?: string }).prefix || ''}
            onChange={e => onUpdate({ prefix: e.target.value || undefined } as Partial<Source>)}
            w="70px"
          />
        </>
      ) : (
        <Input
          size="xs"
          placeholder="Literal value (string, number, or JSON)"
          value={String((source as { value: unknown }).value ?? '')}
          onChange={e => onUpdate({ value: e.target.value } as Partial<Source>)}
          flex="1"
        />
      )}
      <HStack spacing={0.5}>
        <IconButton aria-label="move up"   size="xs" variant="ghost" icon={<span>▲</span>} onClick={onUp}   isDisabled={idx === 0} />
        <IconButton aria-label="move down" size="xs" variant="ghost" icon={<span>▼</span>} onClick={onDown} isDisabled={idx === total - 1} />
        <IconButton aria-label="remove"    size="xs" variant="ghost" colorScheme="red" icon={<span>×</span>} onClick={onRemove} />
      </HStack>
    </HStack>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <Icon viewBox="0 0 24 24" w="12px" h="12px" color="brand.muted" transform={open ? 'rotate(90deg)' : 'rotate(0deg)'} transition="transform 120ms">
      <path fill="currentColor" d="M9 6l6 6-6 6z" />
    </Icon>
  );
}
function StackIcon() {
  return (
    <Icon viewBox="0 0 24 24" w="16px" h="16px" color="rsViolet.500">
      <path fill="currentColor" d="M12 2L2 7l10 5 10-5-10-5zm0 8L2 15l10 5 10-5-10-5zm0 8L2 19l10 3 10-3-10-1z" />
    </Icon>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function cloneSources(sources: Source[]): Source[] {
  return sources.map(s => ({ ...s }));
}

// Structural equality — Source objects always have the same shape once
// normalized by the backend, so a JSON compare is safe.
function sourcesEqual(a: Source[] | undefined, b: Source[] | undefined): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
